import { useTranslation } from 'react-i18next';
import { Badge } from '../../../ui/Badge/index.js';
import { Button } from '../../../ui/Button/index.js';
import { Input } from '../../../ui/Input/index.js';
import { Select } from '../../../ui/Select/index.js';
import type { FilterType } from '../../../../lib/data-viewer-filter.js';
import type {
  DataViewerTable,
  DataViewerTableColumn,
  FilterableColumn,
} from '../../../../types/api.js';

/** フィルタ型ごとの表示ラベル i18n キー（FilterPanel と同じキーを再利用）。 @graph-connects none */
export const TYPE_LABEL_KEYS: Record<FilterType, string> = {
  date: 'dataViewer.filter.typeDate',
  number: 'dataViewer.filter.typeNumber',
  string: 'dataViewer.filter.typeString',
};

/**
 * フィルタ可能カラム編集用の1行。
 *
 * 指定の仕方が 2 通りある。
 *
 * - **DB 内カラムから選択**（`table` あり）: SQL が参照しているテーブルを選び、その実カラムを
 *   `DESCRIBE` 由来の一覧から選ぶ。型は DB の型表記から判定済みのものが降ってくるので、
 *   送信時の型推論は要らない。`SELECT` に出ていないカラムも指定でき、実行時は集約前の
 *   生データに条件がかかる。
 * - **カラム名を手入力**（`table` なし）: 従来どおり実行結果のカラム名を打つ。型は送信時に
 *   実データから判定する（それまでは「未判定」表示）。
 */
export interface FilterableColumnRow {
  /** 行の識別子。カラム名は編集中に変わりうるため React key はこの id で安定させる */
  id: string;
  /** 対象テーブル名。DB 内カラムから選んだ行だけが持つ */
  table: string | null;
  /** カラム名 */
  column: string;
  /** 型。DB 内カラムは選択時に確定する。手入力の新規行・カラム名変更後は null（未判定） */
  type: FilterType | null;
}

/**
 * 行の識別子を発行する。Toast の id 発行（{@link ../../../ui/Toast/Toast.tsx}）と同じ方式。
 *
 * @graph-connects none
 */
function generateRowId(): string {
  return Math.random().toString(36).substring(2, 11);
}

/** 「カラムを追加」クリック時に追加する空行を1件生成する。 @graph-connects none */
export function createEmptyFilterableColumnRow(): FilterableColumnRow {
  return { id: generateRowId(), table: null, column: '', type: null };
}

/** 「DB内カラムから選択」クリック時に追加する、テーブル未選択の空行を1件生成する。 @graph-connects none */
export function createEmptyDatabaseColumnRow(): FilterableColumnRow {
  return { id: generateRowId(), table: '', column: '', type: null };
}

/**
 * 保存済み filterableColumns から編集モードの初期行一覧を組み立てる。
 * 保存済みの型をそのままバッジ表示する。
 *
 * @graph-connects none
 */
export function buildInitialFilterableColumnRows(saved: FilterableColumn[]): FilterableColumnRow[] {
  return saved.map((filterableColumn) => ({
    id: generateRowId(),
    table: filterableColumn.table ?? null,
    column: filterableColumn.column,
    type: filterableColumn.type,
  }));
}

/**
 * 送信用に、手入力された（＝型を実データから判定する必要がある）カラム名を抽出する。
 *
 * DB 内カラムから選んだ行は型が確定済みなので含めない。カラム名が空白のみの行は除外し、
 * 前後の空白は取り除く。同名カラムは先に現れた行を優先する（dedupe）。
 *
 * @graph-connects none
 */
export function collectFilterableColumnNames(rows: FilterableColumnRow[]): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const row of rows) {
    if (row.table !== null) continue;
    const column = row.column.trim();
    if (column === '' || seen.has(column)) continue;
    seen.add(column);
    names.push(column);
  }
  return names;
}

/**
 * 送信用に、DB 内カラムから選ばれた（＝型が確定済みの）カラムを抽出する。
 *
 * テーブル・カラム・型が全て埋まっている行だけを対象にする。
 *
 * @graph-connects none
 */
export function collectDatabaseFilterableColumns(rows: FilterableColumnRow[]): FilterableColumn[] {
  const seen = new Set<string>();
  const columns: FilterableColumn[] = [];
  for (const row of rows) {
    if (row.table === null || row.table === '' || row.column === '' || row.type === null) continue;
    const key = `${row.table}.${row.column}`;
    if (seen.has(key)) continue;
    seen.add(key);
    columns.push({ table: row.table, column: row.column, type: row.type });
  }
  return columns;
}

export interface FilterableColumnsEditorProps {
  /** 編集中のフィルタ可能カラム行一覧 */
  rows: FilterableColumnRow[];
  /** SQL が参照しているテーブル（「DB内カラムから選択」で選べる候補） */
  tables: DataViewerTable[];
  /** テーブルごとのカラム一覧（キーはテーブル名）。取得済みのものだけ入る */
  columnsByTable: Record<string, DataViewerTableColumn[]>;
  /** テーブル一覧を取得中か */
  isLoadingTables: boolean;
  /** カラム一覧を取得中のテーブル名。取得中でなければ null */
  loadingColumnsTable: string | null;
  /** 「カラムを追加」（手入力行）クリック時のハンドラ */
  onAddRow: () => void;
  /** 「DB内カラムから選択」クリック時のハンドラ */
  onAddDatabaseRow: () => void;
  /** 行削除時のハンドラ */
  onRemoveRow: (id: string) => void;
  /** カラム名入力変更時のハンドラ（手入力行） */
  onColumnNameChange: (id: string, column: string) => void;
  /** テーブル選択変更時のハンドラ */
  onTableChange: (id: string, table: string) => void;
  /** カラム選択変更時のハンドラ（DB 内カラム行） */
  onDatabaseColumnChange: (id: string, column: string) => void;
}

/**
 * クエリ登録画面の「フィルタ（任意）」編集セクション。
 *
 * 「DB内カラムから選択」を押すと、SQL が参照しているテーブルの一覧から選び、そのテーブルの
 * 実カラムを `DESCRIBE` 由来の一覧から選べる。`SELECT` に出ていないカラムを登録できるのが
 * 手入力との違いで、そこで選んだカラムは実行時に集約前の生データへ条件がかかる。
 *
 * 従来どおりカラム名を手入力する行も残してあり、そちらは実行結果に対する絞り込みになる。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business クエリ登録画面で、DB のテーブル・カラムから選ぶかカラム名を手入力してフィルタ対象を指定するUIを提供する
 * @graph-connects none
 */
export function FilterableColumnsEditor({
  rows,
  tables,
  columnsByTable,
  isLoadingTables,
  loadingColumnsTable,
  onAddRow,
  onAddDatabaseRow,
  onRemoveRow,
  onColumnNameChange,
  onTableChange,
  onDatabaseColumnChange,
}: FilterableColumnsEditorProps) {
  const { t } = useTranslation();

  const filterableTables = tables.filter((table) => table.filterable);
  const tableOptions = filterableTables.map((table) => ({
    value: table.name,
    label: table.alias === null ? table.name : `${table.name}（${table.alias}）`,
  }));

  return (
    <>
      <p className="text-sm font-semibold text-[var(--color-text-primary)] pt-2">
        {t('dataViewerRegister.filterableColumns')}
      </p>
      <p className="text-xs text-[var(--color-text-muted)]">
        {t('dataViewerRegister.filterableColumnsHelp')}
      </p>
      <div className="space-y-3 rounded-lg border border-[var(--color-border)] p-4">
        {rows.length > 0 && (
          <div className="space-y-2">
            {rows.map((row, index) =>
              row.table === null ? (
                <div key={row.id} className="flex items-center gap-2">
                  <Input
                    value={row.column}
                    onChange={(e) => {
                      onColumnNameChange(row.id, e.target.value);
                    }}
                    placeholder={t('dataViewerRegister.columnNamePlaceholder')}
                    aria-label={t('dataViewerRegister.columnNameInputLabel', {
                      index: index + 1,
                    })}
                    size="sm"
                    fullWidth
                  />
                  <Badge variant="default" size="sm">
                    {row.type ? t(TYPE_LABEL_KEYS[row.type]) : t('dataViewerRegister.typeUnknown')}
                  </Badge>
                  <button
                    type="button"
                    onClick={() => {
                      onRemoveRow(row.id);
                    }}
                    aria-label={t('dataViewerRegister.removeColumn', { index: index + 1 })}
                    className="shrink-0 px-1 text-[var(--color-text-muted)] hover:text-[var(--color-error)] transition-colors"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div key={row.id} className="flex items-center gap-2">
                  <Select
                    options={tableOptions}
                    placeholder={
                      isLoadingTables
                        ? t('dataViewerRegister.loadingTables')
                        : t('dataViewerRegister.selectTablePlaceholder')
                    }
                    value={row.table}
                    onChange={(e) => {
                      onTableChange(row.id, e.target.value);
                    }}
                    aria-label={t('dataViewerRegister.tableSelectLabel', { index: index + 1 })}
                    size="sm"
                    fullWidth
                  />
                  <Select
                    options={(columnsByTable[row.table] ?? []).map((column) => ({
                      value: column.field,
                      label: `${column.field}（${column.dbType}）`,
                    }))}
                    placeholder={
                      loadingColumnsTable === row.table
                        ? t('dataViewerRegister.loadingColumns')
                        : t('dataViewerRegister.selectColumnPlaceholder')
                    }
                    value={row.column}
                    onChange={(e) => {
                      onDatabaseColumnChange(row.id, e.target.value);
                    }}
                    disabled={row.table === '' || loadingColumnsTable === row.table}
                    aria-label={t('dataViewerRegister.columnSelectLabel', { index: index + 1 })}
                    size="sm"
                    fullWidth
                  />
                  <Badge variant="default" size="sm">
                    {row.type ? t(TYPE_LABEL_KEYS[row.type]) : t('dataViewerRegister.typeUnknown')}
                  </Badge>
                  <button
                    type="button"
                    onClick={() => {
                      onRemoveRow(row.id);
                    }}
                    aria-label={t('dataViewerRegister.removeColumn', { index: index + 1 })}
                    className="shrink-0 px-1 text-[var(--color-text-muted)] hover:text-[var(--color-error)] transition-colors"
                  >
                    ×
                  </button>
                </div>
              ),
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={onAddDatabaseRow}>
            {t('dataViewerRegister.addDatabaseColumn')}
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={onAddRow}>
            {t('dataViewerRegister.addColumn')}
          </Button>
        </div>
      </div>
    </>
  );
}
